import { ReactNode } from "react";
import { SortOrder } from "~/components/ui/SortOrder";
import css from "./Sorting.module.css";
import { useSetUrlQuery, useUrlQuery } from "./UrlUtils";

export type SortingOrder = "asc" | "desc";

export type SortBy = {
  value: string[];
  update: (list: string[]) => void;
};

export function useUrlSorting(): SortBy {
  const urlQuery = useUrlQuery();
  const setUrlQuery = useSetUrlQuery();

  return {
    value: urlQuery["s"] ? urlQuery["s"].split(",", 10) : [],
    update: (next) =>
      setUrlQuery({ ...urlQuery, s: next.join(",") || undefined }),
  };
}

export function SortColumnName(props: {
  name: string;
  by: SortBy;
  children: ReactNode;
}) {
  const name = props.name;

  let order: SortingOrder | null = null;
  const sorters = props.by.value;
  if (sorters.includes(name)) {
    order = "asc";
  } else if (sorters.includes("-" + name)) {
    order = "desc";
  }

  return (
    <span
      className={css.column}
      onMouseDown={(e) => {
        if (e.shiftKey) {
          // Prevent text selection.
          e.preventDefault();
        }
      }}
      onClick={(e) => {
        let next: string[] = [];
        if (e.shiftKey) {
          next = sorters
            .filter((x) => x !== name && x !== "-" + name)
            .slice(0, 1);
        }

        if (order === "asc") {
          next.push("-" + name);
        } else if (order === "desc") {
          // Remove sorter if it was in the DESC state
        } else {
          next.push(name);
        }

        props.by.update(next);
      }}
    >
      {props.children}
      {order && <SortOrder value={order} />}
    </span>
  );
}

export function createSorting<T>(
  fields: Record<string, (a: T, b: T) => number>
) {
  return {
    apply(items: ReadonlyArray<T>, s: string | undefined) {
      const sorters: [number, (a: T, b: T) => number][] = [];
      for (let str of s?.split(",", 4) ?? []) {
        let order = 1;
        if (str.startsWith("-")) {
          order = -1;
          str = str.slice(1);
        }
        const f = fields[str];
        if (f) {
          sorters.push([order, f]);
        }
      }
      if (sorters.length > 0) {
        return items.slice().sort((a, b) => {
          for (const [order, f] of sorters) {
            const comp = f(a, b);
            if (comp !== 0) {
              return comp * order;
            }
          }
          return 0;
        });
      }
      return items;
    },
  };
}
